"use client";

import {
  Bell,
  Calendar,
  CalendarCheck,
  Check,
  ClipboardList,
  Heart,
  Home,
  LayoutDashboard,
  MapPin,
  MessageCircle,
  Monitor,
  Play,
  Plug,
  Receipt,
  Search,
  Share2,
  Smartphone,
  Star,
  Users,
  Wrench,
  Zap,
} from "lucide-react";
import { BrowserFrame, PhoneFrame } from "./frames";

/* ─────────────────────────────────────────────────────────────
 * Die drei großen Schau-Mockups: Website, Software, Marketing.
 *
 * Grundsatz für ALLE Mockups der Seite: Sie zeigen, WIE eine Lösung
 * aussieht – nicht, was sie angeblich gebracht hat. Keine erfundenen
 * Ergebnis-Zahlen („+150 %“, „84k Views“, „ROAS 5,4ד). Beispieldaten
 * gehören zu einem erkennbaren Beispielbetrieb (beispielbetrieb.example).
 * ──────────────────────────────────────────────────────────── */

export const SAMPLE_BUSINESS = "Beispielbetrieb GmbH";
export const SAMPLE_HOST = "beispielbetrieb.example";

/* ─────────────────────────────────────────────────────────────
 * 1) Website: echte Seitenstruktur eines Handwerksbetriebs –
 *    Navigation, klare Aussage, Kontakt-Button, Vertrauens-Chips,
 *    Leistungskarten. Dazu die Handy-Ansicht und eine eingehende
 *    Anfrage als Hinweis darauf, wofür die Seite gebaut ist.
 * ──────────────────────────────────────────────────────────── */
export function WebsiteMockup() {
  return (
    <div
      className="relative overflow-visible rounded-2xl border p-4 pb-10 sm:p-6 sm:pb-12"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
    >
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-400/15 via-transparent to-emerald-400/10" />

      <BrowserFrame host={`www.${SAMPLE_HOST}`} className="relative mr-6 sm:mr-12">
        {/* Navigation */}
        <div className="flex items-center justify-between border-b px-4 py-2" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center rounded" style={{ backgroundColor: "var(--color-brand-500)" }}>
              <Wrench className="h-2.5 w-2.5 text-white" />
            </span>
            <span className="text-[10px] font-bold" style={{ color: "var(--text)" }}>
              {SAMPLE_BUSINESS}
            </span>
          </div>
          <div className="hidden items-center gap-3 text-[9px] sm:flex" style={{ color: "var(--text-tertiary)" }}>
            <span>Leistungen</span>
            <span>Referenzen</span>
            <span>Über uns</span>
            <span>Kontakt</span>
          </div>
          <span className="rounded px-2 py-0.5 text-[9px] font-semibold text-white" style={{ backgroundColor: "var(--color-brand-700)" }}>
            Termin anfragen
          </span>
        </div>

        {/* Hero */}
        <div className="grid gap-3 px-4 py-4 sm:grid-cols-5">
          <div className="sm:col-span-3">
            <span
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[8px] font-semibold"
              style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}
            >
              <Check className="h-2 w-2" />
              Meisterbetrieb · seit 1987
            </span>
            <p className="mt-1.5 text-[13px] font-bold leading-tight sm:text-sm" style={{ color: "var(--text)" }}>
              Heizung, Sanitär und Bad – aus einer Hand, aus Ihrer Region.
            </p>
            <p className="mt-1 text-[9px] leading-snug" style={{ color: "var(--text-tertiary)" }}>
              Wartung, Reparatur, Neubau. Schriftliches Angebot, verlässliche Termine.
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="rounded px-2 py-1 text-[9px] font-semibold text-white" style={{ backgroundColor: "var(--color-brand-700)" }}>
                Termin anfragen
              </span>
              <span className="rounded border px-2 py-1 text-[9px] font-semibold" style={{ borderColor: "var(--border-secondary)", color: "var(--text-secondary)" }}>
                Notdienst
              </span>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1">
              {["Festpreis-Angebot", "Rückruf am selben Tag", "Regional"].map((t) => (
                <span key={t} className="inline-flex items-center gap-0.5 text-[8px] font-medium" style={{ color: "var(--text-secondary)" }}>
                  <Check className="h-2 w-2 text-brand-500" />
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div
            className="hidden overflow-hidden rounded-lg sm:col-span-2 sm:flex sm:items-center sm:justify-center"
            style={{ background: "linear-gradient(135deg, var(--color-brand-400), #0E7490)" }}
          >
            <Wrench className="h-8 w-8 text-white/80" />
          </div>
        </div>

        {/* Leistungen */}
        <div className="grid grid-cols-3 gap-2 border-t px-4 py-3" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
          {[
            { Icon: Zap, t: "Heizung" },
            { Icon: Home, t: "Bad & Sanitär" },
            { Icon: Wrench, t: "Wartung" },
          ].map(({ Icon, t }) => (
            <div key={t} className="rounded-lg border p-2" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
              <span className="flex h-5 w-5 items-center justify-center rounded" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
                <Icon className="h-3 w-3" />
              </span>
              <p className="mt-1.5 text-[9px] font-semibold" style={{ color: "var(--text)" }}>
                {t}
              </p>
              <div className="mt-1 h-1 w-4/5 rounded-full" style={{ backgroundColor: "var(--border)" }} />
            </div>
          ))}
        </div>
      </BrowserFrame>

      {/* Eingehende Anfrage – wofür die Seite gebaut ist */}
      <div
        className="absolute -top-3 right-2 flex w-[190px] items-start gap-2 rounded-xl border p-2.5 sm:right-4 sm:w-[210px]"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-lg)" }}
      >
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}>
          <Bell className="h-3 w-3" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold" style={{ color: "var(--text)" }}>
            Neue Anfrage über die Website
          </p>
          <p className="truncate text-[9px]" style={{ color: "var(--text-tertiary)" }}>
            Badsanierung · Rückruf gewünscht
          </p>
        </div>
      </div>

      {/* Handy-Ansicht – die Seite funktioniert mobil zuerst */}
      <PhoneFrame width={104} className="absolute -bottom-4 right-3 sm:right-6">
        <div className="px-2 pt-5">
          <div className="flex items-center justify-between">
            <span className="h-2 w-2 rounded" style={{ backgroundColor: "var(--color-brand-500)" }} />
            <div className="space-y-0.5">
              <span className="block h-0.5 w-3 rounded" style={{ backgroundColor: "var(--text-tertiary)" }} />
              <span className="block h-0.5 w-3 rounded" style={{ backgroundColor: "var(--text-tertiary)" }} />
            </div>
          </div>
          <div className="mt-2 h-10 rounded-md" style={{ background: "linear-gradient(135deg, var(--color-brand-400), #0E7490)" }} />
          <p className="mt-1.5 text-[7px] font-bold leading-tight" style={{ color: "var(--text)" }}>
            Heizung, Sanitär und Bad aus Ihrer Region.
          </p>
          <div className="mt-1 h-1 w-4/5 rounded-full" style={{ backgroundColor: "var(--border)" }} />
          <div className="mt-0.5 h-1 w-3/5 rounded-full" style={{ backgroundColor: "var(--border)" }} />
          <div className="mt-2 rounded px-1.5 py-1 text-center text-[7px] font-semibold text-white" style={{ backgroundColor: "var(--color-brand-700)" }}>
            Termin anfragen
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-1">
            {[1, 2].map((i) => (
              <div key={i} className="rounded border p-1" style={{ borderColor: "var(--border)" }}>
                <span className="block h-2 w-2 rounded" style={{ backgroundColor: "var(--fi-brand-bg)" }} />
                <span className="mt-1 block h-0.5 w-4/5 rounded" style={{ backgroundColor: "var(--border)" }} />
              </div>
            ))}
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 2) Software: internes Werkzeug (Aufträge, Termine, Kunden) plus
 *    Kunden-App zum Termin buchen. Beispieldaten eines Beispielbetriebs.
 * ──────────────────────────────────────────────────────────── */
const JOBS = [
  { customer: "Familie Weber", job: "Wartung Heizung", status: "Erledigt", tone: "success" as const },
  { customer: "Praxis Dr. Lang", job: "Badsanierung", status: "Unterwegs", tone: "brand" as const },
  { customer: "Bäckerei Roth", job: "Angebot Wärmepumpe", status: "Geplant", tone: "gray" as const },
];

const TONE: Record<"success" | "brand" | "gray", { bg: string; fg: string }> = {
  success: { bg: "var(--fi-success-bg)", fg: "var(--fi-success-fg)" },
  brand: { bg: "var(--fi-brand-bg)", fg: "var(--fi-brand-fg)" },
  gray: { bg: "var(--fi-gray-bg)", fg: "var(--fi-gray-fg)" },
};

export function SoftwareMockup() {
  return (
    <div
      className="relative overflow-visible rounded-2xl border p-4 pb-8 sm:p-6 sm:pb-10"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
    >
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-sky-400/10 via-transparent to-brand-400/15" />

      <BrowserFrame host={`app.${SAMPLE_HOST}`} className="relative mr-24">
        <div className="flex">
          {/* Seitenleiste */}
          <div className="hidden w-[92px] shrink-0 border-r px-2 py-2.5 sm:block" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
            {[
              { Icon: LayoutDashboard, t: "Übersicht", active: true },
              { Icon: ClipboardList, t: "Aufträge" },
              { Icon: Calendar, t: "Termine" },
              { Icon: Users, t: "Kunden" },
              { Icon: Receipt, t: "Rechnungen" },
            ].map(({ Icon, t, active }) => (
              <div
                key={t}
                className="mb-0.5 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[9px] font-medium"
                style={
                  active
                    ? { backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }
                    : { color: "var(--text-tertiary)" }
                }
              >
                <Icon className="h-2.5 w-2.5" />
                {t}
              </div>
            ))}
          </div>

          {/* Inhalt */}
          <div className="flex-1 p-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold" style={{ color: "var(--text)" }}>
                Heute, Dienstag
              </p>
              <span className="rounded px-1.5 py-0.5 text-[8px] font-semibold text-white" style={{ backgroundColor: "var(--color-brand-700)" }}>
                + Auftrag
              </span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {[
                { l: "Termine heute", v: "6" },
                { l: "Teams unterwegs", v: "2" },
                { l: "Offene Angebote", v: "3" },
              ].map((k) => (
                <div key={k.l} className="rounded-md border p-1.5" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
                  <p className="text-[8px]" style={{ color: "var(--text-tertiary)" }}>
                    {k.l}
                  </p>
                  <p className="text-sm font-bold leading-tight" style={{ color: "var(--text)" }}>
                    {k.v}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-2 overflow-hidden rounded-md border" style={{ borderColor: "var(--border)" }}>
              {JOBS.map((j, i) => (
                <div
                  key={j.customer}
                  className="flex items-center justify-between gap-2 px-2 py-1.5"
                  style={{ borderTop: i ? "1px solid var(--border)" : undefined }}
                >
                  <div className="min-w-0">
                    <p className="truncate text-[9px] font-semibold" style={{ color: "var(--text)" }}>
                      {j.customer}
                    </p>
                    <p className="truncate text-[8px]" style={{ color: "var(--text-tertiary)" }}>
                      {j.job}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[8px] font-semibold" style={{ backgroundColor: TONE[j.tone].bg, color: TONE[j.tone].fg }}>
                    {j.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </BrowserFrame>

      {/* Kunden-App: Termin buchen */}
      <PhoneFrame width={104} className="absolute -bottom-3 right-4 sm:right-3">
        <div className="px-2.5 pt-5">
          <p className="text-[8px] font-semibold" style={{ color: "var(--text-tertiary)" }}>
            Kunden-App
          </p>
          <p className="text-[9px] font-bold" style={{ color: "var(--text)" }}>
            Termin buchen
          </p>
          <div className="mt-2 grid grid-cols-3 gap-1">
            {["08:00", "10:30", "13:00", "14:30", "16:00"].map((t, i) => (
              <span
                key={t}
                className="rounded px-1 py-1 text-center text-[7px] font-semibold"
                style={
                  i === 1
                    ? { backgroundColor: "var(--color-brand-700)", color: "white" }
                    : { border: "1px solid var(--border)", color: "var(--text-secondary)" }
                }
              >
                {t}
              </span>
            ))}
          </div>
          <div className="mt-2 rounded px-1.5 py-1 text-center text-[7px] font-semibold text-white" style={{ backgroundColor: "var(--color-brand-700)" }}>
            Bestätigen
          </div>
          <div className="mt-2.5 flex items-start gap-1 rounded-md border p-1.5" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
            <CalendarCheck className="mt-0.5 h-2.5 w-2.5 shrink-0 text-brand-500" />
            <p className="text-[7px] leading-snug" style={{ color: "var(--text-secondary)" }}>
              Ihr Techniker ist unterwegs – Ankunft ca. 10:30 Uhr.
            </p>
          </div>
        </div>
      </PhoneFrame>

      {/* Schnittstellen & Plattformen */}
      <div className="relative mt-4 flex flex-wrap items-center gap-1.5 pr-28">
        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
          <Plug className="h-3 w-3 text-brand-500" />
          Schnittstellen: Buchhaltung · Kalender · Portale
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
          <Monitor className="h-3 w-3 text-brand-500" />
          Web · Windows/Mac
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
          <Smartphone className="h-3 w-3 text-brand-500" />
          iOS · Android
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 3) Marketing: Google-Suche (Anzeige, Maps-Eintrag, organischer
 *    Treffer) und ein kurzes Video aus dem Betrieb auf Social Media.
 *    Keine Reichweiten- oder Ergebnis-Zahlen.
 * ──────────────────────────────────────────────────────────── */
export function Stars({ n, className = "h-2 w-2" }: { n: number; className?: string }) {
  return (
    <span className="inline-flex gap-px" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`${className} ${i < n ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
      ))}
    </span>
  );
}

export function MarketingMockup() {
  return (
    <div
      className="relative overflow-visible rounded-2xl border p-4 pb-6 sm:p-6"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
    >
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400/10 via-transparent to-pink-400/10" />

      <div className="relative grid gap-4 sm:grid-cols-5">
        {/* Google-Suche */}
        {/* min-w-0: der nowrap-Titel (truncate) im organischen Treffer darf
            die Spalte nicht breiter machen als den Rahmen – sonst ragt das
            Mockup auf dem Handy über den Seitenrand. */}
        <div className="min-w-0 sm:col-span-3">
          <BrowserFrame host="google.de">
            <div className="px-3 py-3">
              <div className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px]" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)", color: "var(--text-secondary)" }}>
                <Search className="h-3 w-3" />
                <span className="truncate">heizung sanitär bad neustadt</span>
              </div>

              {/* Anzeige */}
              <div className="mt-3 border-l-2 pl-2.5" style={{ borderColor: "#1A73E8" }}>
                <div className="flex items-center gap-1.5">
                  <span className="rounded-sm px-1 text-[8px] font-bold text-white" style={{ backgroundColor: "#1A73E8" }}>
                    Anzeige
                  </span>
                  <span className="text-[9px]" style={{ color: "var(--text-tertiary)" }}>
                    {SAMPLE_HOST}
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] font-medium" style={{ color: "#1A0DAB" }}>
                  Heizung & Bad vom Meisterbetrieb – jetzt Termin anfragen
                </p>
              </div>

              {/* Maps / Local Pack */}
              <div className="mt-3 rounded-lg border p-2" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
                <div className="flex items-center justify-between">
                  <p className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
                    <MapPin className="h-2.5 w-2.5" />
                    Unternehmen in Ihrer Nähe
                  </p>
                  <span className="h-6 w-10 rounded" style={{ background: "linear-gradient(135deg, #C7E9C0, #A8DADC)" }} />
                </div>
                <div className="mt-1.5 space-y-1">
                  {[
                    { name: SAMPLE_BUSINESS, stars: 5, first: true, meta: "Geöffnet · Salz" },
                    { name: "Mitbewerber A", stars: 4, first: false, meta: "Bad Neustadt" },
                    { name: "Mitbewerber B", stars: 4, first: false, meta: "Mellrichstadt" },
                  ].map((r) => (
                    <div
                      key={r.name}
                      className="flex items-center justify-between rounded px-2 py-1"
                      style={r.first ? { backgroundColor: "var(--fi-brand-bg)", border: "1px solid var(--fi-brand-fg)" } : undefined}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[10px] font-semibold" style={{ color: r.first ? "var(--fi-brand-fg)" : "var(--text-secondary)" }}>
                          {r.name}
                        </p>
                        <p className="text-[8px]" style={{ color: "var(--text-tertiary)" }}>
                          {r.meta}
                        </p>
                      </div>
                      <Stars n={r.stars} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Organisch */}
              <div className="mt-3 rounded-lg border p-2" style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[9px]" style={{ color: "var(--text-tertiary)" }}>
                      {SAMPLE_HOST} › leistungen
                    </p>
                    <p className="truncate text-[11px] font-medium" style={{ color: "#1A0DAB" }}>
                      Heizung, Sanitär & Bad – {SAMPLE_BUSINESS}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white" style={{ backgroundColor: "var(--fi-brand-fg)" }}>
                    Platz 1
                  </span>
                </div>
              </div>
            </div>
          </BrowserFrame>
        </div>

        {/* Social: kurzes Video aus dem Betrieb */}
        <div className="flex min-w-0 items-start justify-center sm:col-span-2 sm:justify-end">
          <PhoneFrame width={150} className="relative">
            <div className="relative h-full w-full" style={{ background: "linear-gradient(160deg, #0F766E 0%, #1D4ED8 55%, #7C3AED 100%)" }}>
              <div className="absolute left-2 top-5 rounded bg-white/90 px-1.5 py-0.5 text-[8px] font-bold text-black">Reel</div>
              <div className="absolute left-1/2 top-[38%] flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-white/25 backdrop-blur">
                <Play className="ml-0.5 h-4 w-4 fill-white text-white" />
              </div>
              <div className="absolute right-1.5 top-[46%] space-y-2.5 text-white">
                <Heart className="h-3.5 w-3.5" />
                <MessageCircle className="h-3.5 w-3.5" />
                <Share2 className="h-3.5 w-3.5" />
              </div>
              <div className="absolute inset-x-2 bottom-3 text-white">
                <p className="text-[8px] font-semibold">@muster.haustechnik</p>
                <p className="mt-0.5 text-[8px] leading-snug text-white/90">
                  Einblick aus der Werkstatt: So läuft eine Heizungswartung bei uns ab.
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {["#Handwerk", "#Ausbildung", "#Rhön"].map((h) => (
                    <span key={h} className="rounded bg-white/15 px-1 py-0.5 text-[7px]">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </PhoneFrame>
        </div>
      </div>

      {/* Bericht: was gemessen wird – nicht, was angeblich herauskam */}
      <div className="relative mt-4 flex flex-wrap items-center gap-1.5">
        {["Google Ads", "Google Maps & Suche", "Instagram, Facebook, LinkedIn", "Monatlicher Bericht: Kosten und Anfragen"].map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", color: "var(--text-secondary)" }}>
            <Check className="h-3 w-3 text-brand-500" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

